import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import React from "react";
import { KeyValue, SubOption } from "../../interfaces";

import clsx from "clsx";
import { reduceObj } from "../../common/services/Utilities";

const PREFIX = "SubSelectionCheckForm";

const classes = {
  popperStyles: `${PREFIX}-popperStyles`,
  activeLabel: `${PREFIX}-activeLabel`,
  label: `${PREFIX}-label`,
  largePopup: `${PREFIX}-largePopup`,
  mediumPopup: `${PREFIX}-mediumPopup`,
  smallPopup: `${PREFIX}-smallPopup`,
  popup: `${PREFIX}-popup`,
  paper: `${PREFIX}-paper`,
  labelOnFocus: `${PREFIX}-labelOnFocus`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.popperStyles}`]: {
    "zIndex": 100,
    "marginLeft": -2,
    "& .MuiFormGroup-root": {
      [theme.breakpoints.down("md")]: {
        width: 300,
      },
    },
  },

  [`& .${classes.activeLabel}`]: {
    "&.MuiButtonBase-root": {
      transition: ".6s ease",
      background: "#fbfcfd",
      boxShadow: `2px 2px 0px -2px ${theme.palette.primary.main}`,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,

      [theme.breakpoints.down("md")]: {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
  },

  [`& .${classes.label}`]: {
    "width": "120%",
    "zIndex": 101,
    "padding": 8,
    "height": "auto",
    "alignItems": "center",
    "& .MuiGrid-item": {
      display: "inline-flex",
    },
    "& p": {
      textAlign: "left",
      color: theme.palette.primary.main,
      fontSize: "1rem",
    },
    "& svg": {
      position: "absolute",
      right: 0,
      color: theme.palette.primary.main,
    },
    "&.Mui-disabled p,&.Mui-disabled  svg": {
      color: "rgba(0, 0, 0, 0.26)",
    },
  },

  [`& .${classes.largePopup}`]: {
    "& .MuiFormGroup-root": {
      width: 1000,
      maxHeight: 575,
      [theme.breakpoints.down("md")]: {
        width: "100%",
        overflow: "scroll",
        display: "grid",
      },
    },
  },

  [`& .${classes.mediumPopup}`]: {
    "& .MuiFormGroup-root": {
      width: 500,
      maxHeight: 500,
    },
  },

  [`& .${classes.smallPopup}`]: {
    "& .MuiBox-root": { paddingLeft: 20 },
    "& .MuiFormGroup-root": {
      maxWidth: 1000,
      maxHeight: 500,
      display: "grid",
      gridTemplateRows: "auto",
      gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
      overflow: "auto",
    },
  },

  [`& .${classes.popup}`]: {
    "position": "relative",
    [theme.breakpoints.down("md")]: {
      borderRadius: 0,
      border: `1px solid ${theme.palette.primary.light}`,
    },
    "& .MuiFormGroup-root": {
      padding: 15,
    },
  },

  [`& .${classes.paper}`]: {
    marginLeft: 40,
    boxShadow: `2px 2px 0px 0px ${theme.palette.primary.light}`,
    borderRadius: 0,
    maxHeight: "400px",
    width: "auto",
    minWidth: 250,
    maxWidth: 500,
    overflow: "scroll",
  },

  [`& .${classes.labelOnFocus}`]: {
    border: `1px solid ${theme.palette.primary.light}`,
  },
}));

interface Props {
  type: string;
  options: SubOption[];
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  checkedStatus: KeyValue;
  setCheckedStatus: (checkedStatus: KeyValue) => void;
  subCaption: string;
  indeterminate: boolean;
}

const handleSelectAll = (
  options: any,
  setSelection: (checkedStatus: KeyValue) => void,
) => {
  let stringArray = false;
  if (options.every((item: any) => typeof item === "string")) {
    stringArray = true;
  }
  setSelection((prev: KeyValue) => {
    const numSelected = Object.values(prev).filter(
      (value) => value === true,
    ).length;
    if (numSelected >= 0 && numSelected !== options.length) {
      return reduceObj(
        options.map((option: SubOption) => {
          return { key: stringArray ? option : option.value, value: true };
        }),
      );
    } else {
      return reduceObj(
        options.map((option: any) => {
          return { key: stringArray ? option : option.value, value: false };
        }),
      );
    }
  });
};

export default function SubSelectionCheckForm({
  type,
  options,
  onChange,
  checkedStatus,
  setCheckedStatus,
  subCaption,
  indeterminate,
}: Props) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMouseEnter = (target: any) => {
    setIsHovered(true);
    setAnchorEl(target);
  };

  const handleMouseExit = (target: any) => {
    setIsHovered(false);
    setAnchorEl(target);
  };

  const boxId = `selected-${type.replace(/ /g, "-").toLowerCase()}`;

  return checkedStatus ? (
    <Root>
      <Button
        fullWidth
        onFocus={(e) => {
          e.preventDefault();
          handleMouseEnter(e.currentTarget);
        }}
        onBlur={(e) => {
          e.preventDefault();
          const target = e.relatedTarget;
          if (target?.contains(target)) {
            return;
          }
          handleMouseExit(e.currentTarget);
        }}
        onMouseEnter={(e) => handleMouseEnter(e.currentTarget)}
        onMouseLeave={(e) => handleMouseExit(e.currentTarget)}
        sx={{ marginY: 1, marginLeft: -1 }}
      >
        <Box id="hover-box" sx={{ width: "100%" }}>
          <Box
            id={`${boxId}_button`}
            aria-controls={isHovered ? boxId : undefined}
            aria-haspopup="true"
            className={clsx(
              classes.label,
              isHovered ? classes.activeLabel : "",
            )}
            sx={
              isHovered
                ? {
                    backgroundColor: "white",
                    boxShadow: `2px 2px 4px -2px #7986cb`,
                  }
                : {}
            }
          >
            <Grid container>
              <Grid size={{ xs: 9 }}>
                <Typography>
                  <>
                    <span
                      style={{ display: "block", fontWeight: "bolder" }}
                      id={boxId}
                    >
                      {`Select ${type}`}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 14,
                        color: isHovered ? "transparent" : "#194480",
                      }}
                      id={`all_${boxId}`}
                    >
                      {subCaption}
                    </span>
                  </>
                </Typography>
              </Grid>
              <Grid
                size={{ xs: 3 }}
                sx={{
                  alignItems: "center",
                  marginBottom: 3,
                }}
              >
                {isHovered && (
                  <Typography variant="caption" color="primary.main">
                    edit
                  </Typography>
                )}
                <ChevronRightIcon />
              </Grid>
            </Grid>
          </Box>
          <Popper
            open={isHovered}
            anchorEl={anchorEl}
            id="menu-popup-content"
            className={clsx(classes.popperStyles)}
            placement={"right"}
            disablePortal
          >
            <Paper className={classes.paper} sx={{ width: 250 }}>
              <Box id={boxId} className={classes.popup}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="select_all"
                        disableFocusRipple
                        checked={
                          !!Object.keys(checkedStatus).every(
                            (checked) => checkedStatus[checked],
                          )
                        }
                        indeterminate={indeterminate}
                        onChange={(e: any) =>
                          handleSelectAll(options, setCheckedStatus)
                        }
                        name={"Select All / None"}
                        sx={{
                          color: "#194480",
                        }}
                      />
                    }
                    label={"Select All / None"}
                  />

                  {options.map((e: any, index) => {
                    return (
                      <FormControlLabel
                        key={index}
                        control={
                          <Checkbox
                            id={e.value ?? e}
                            key={index}
                            disabled={false}
                            disableFocusRipple
                            checked={!!checkedStatus[e.value ?? e]}
                            onChange={onChange}
                            name={e.label ?? e}
                            sx={{
                              "color": "#194480",
                              "& .MuiFormControlLabel-label": {
                                whiteSpace: "nowrap",
                              },
                            }}
                          />
                        }
                        label={e.label ?? e}
                        sx={{
                          marginBottom: 0,
                        }}
                      />
                    );
                  })}
                </FormGroup>
              </Box>
            </Paper>
          </Popper>
        </Box>
      </Button>
    </Root>
  ) : (
    <></>
  );
}
